import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  CalendarDays,
  LogIn,
  MedalIcon,
  User,
  Building2,
  Menu,
  X,
  UserPlus
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/auth/AuthContext';
import Logo from './Logo';
import { Button } from '@/components/ui/button';
import { UserRoles } from '@/contexts/auth/enums';
import UserMenu from './UserMenu';

interface NavbarProps {
  variant?: 'default' | 'admin';
  additionalLinks?: React.ReactNode;
  className?: string;
}

const Navbar: React.FC<NavbarProps> = ({
  variant = 'default',
  additionalLinks,
  className
}) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const {
    isAuthenticated,
    user,
    logout,
  } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  const isActivePath = (path: string) => {
    return location.pathname === path;
  };

  const isOrganizer = user?.roles?.includes(UserRoles.ORGANIZER);
  const isAdmin = user?.roles?.includes(UserRoles.ADMIN);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <header className={cn(
      'fixed top-0 left-0 bg-white shadow-lg right-0 z-50 transition-all duration-500 ease-in-out',
      scrolled
        ? '  border-b border-gray-100'
        : ' backdrop-blur-sm py-2',
      className
    )}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between">
          <div className="relative z-10">
            <Logo />
          </div>

          <button
            className="md:hidden p-2 rounded-full focus:outline-none"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>

          <nav className="hidden md:flex items-center space-x-2">
            {variant === 'default' && (
              <>
                <NavLink to="/calendar" active={isActivePath('/calendar')}>
                  <CalendarDays className="w-4 h-4 mr-1" />
                  Egészségügyi rendezvénynaptár
                </NavLink>
                <NavLink to="/why-medihub" active={isActivePath('/why-medihub')}>
                  <MedalIcon className="w-4 h-4 mr-1" />
                  Szakembereknek
                </NavLink>
                {!isOrganizer && <NavLink to="/organizer-guide" active={isActivePath('/organizer-guide')}>
                  <Building2 className="w-4 h-4 mr-1" />
                  Rendezvényszervezőknek
                </NavLink>}
              </>
            )}

            {variant === 'admin' && (
              <>
                <NavLink to="/admin/dashboard" active={isActivePath('/admin/dashboard')}>
                  Admin vezérlőpult
                </NavLink>
                <NavLink to="/admin/events" active={isActivePath('/admin/events')}>
                  Események kezelése
                </NavLink>
                <NavLink to="/admin/users" active={isActivePath('/admin/users')}>
                  Felhasználók
                </NavLink>
              </>
            )}

            {additionalLinks}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            {isAuthenticated ? (
              <UserMenu
                user={user}
                isAdmin={Boolean(isAdmin)}
                isOrganizer={Boolean(isOrganizer)}
                logout={logout}
              />
            ) : (
              <div className="flex items-center gap-2">
                <Link
                  to="/login"
                  className="flex items-center text-sm font-medium px-4 py-2 bg-medihub-accent text-white rounded-full hover:bg-medihub-accent/90 shadow-md hover:shadow-lg hover:shadow-medihub-accent/20 transition-all duration-300"
                >
                  <LogIn className="w-4 h-4 mr-1" />
                  Bejelentkezés
                </Link>
              </div>
            )}
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 space-y-3 bg-white rounded-lg p-4 shadow-xl">
            <div className="space-y-2">
              {variant === 'default' && (
                <>
                  <MobileNavLink to="/" active={isActivePath('/')}>
                    Kezdőlap
                  </MobileNavLink>
                  <MobileNavLink to="/calendar" active={isActivePath('/calendar')}>
                    <CalendarDays className="w-4 h-4 mr-2" />
                    Naptár
                  </MobileNavLink>
                  <MobileNavLink to="/why-medihub" active={isActivePath('/why-medihub')}>
                    <MedalIcon className="w-4 h-4 mr-2" />
                    Szakembereknek
                  </MobileNavLink>
                  {!isOrganizer && (
                    <MobileNavLink to="/organizer-guide" active={isActivePath('/organizer-guide')}>
                      <Building2 className="w-4 h-4 mr-2" />
                      Rendezvényszervezőknek
                    </MobileNavLink>
                  )}
                </>
              )}

              {variant === 'admin' && (
                <>
                  <MobileNavLink to="/admin/dashboard" active={isActivePath('/admin/dashboard')}>
                    Admin vezérlőpult
                  </MobileNavLink>
                  <MobileNavLink to="/admin/events" active={isActivePath('/admin/events')}>
                    Események kezelése
                  </MobileNavLink>
                  <MobileNavLink to="/admin/users" active={isActivePath('/admin/users')}>
                    Felhasználók
                  </MobileNavLink>
                </>
              )}
            </div>

            <div className="pt-2 border-t border-gray-200">
              {isAuthenticated ? (
                <div className="space-y-2">
                  {isAdmin && (
                    <MobileNavLink to="/admin/dashboard" active={isActivePath('/admin/dashboard')}>
                      Admin Panel
                    </MobileNavLink>
                  )}
                  {isOrganizer && (
                    <MobileNavLink to="/organizer/dashboard" active={isActivePath('/organizer/dashboard')}>
                      Szervező vezérlőpult
                    </MobileNavLink>
                  )}
                  <MobileNavLink
                    to={isOrganizer ? "/organizer/profile" : "/participant/profile"}
                    active={isActivePath(isOrganizer ? "/organizer/profile" : "/participant/profile")}
                  >
                    <User className="w-4 h-4 mr-2" />
                    Profil
                  </MobileNavLink>
                  <button
                    onClick={logout}
                    className="w-full flex items-center px-4 py-2 text-sm text-red-600 font-medium rounded-md bg-red-50 hover:bg-red-100"
                  >
                    Kijelentkezés
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <MobileNavLink to="/login" active={isActivePath('/login')}>
                    <LogIn className="w-4 h-4 mr-2" />
                    Bejelentkezés
                  </MobileNavLink>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

interface NavLinkProps {
  to: string;
  active: boolean;
  children: React.ReactNode;
}

const NavLink = ({ to, active, children }: NavLinkProps) => {
  return (
    <Link
      to={to}
      className={cn(
        'flex items-center px-3 py-2 rounded-full text-sm font-medium transition-all',
        active
          ? 'bg-accent text-white'
          : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
      )}
    >
      {children}
    </Link>
  );
};

interface MobileNavLinkProps {
  to: string;
  active: boolean;
  children: React.ReactNode;
  highlight?: boolean;
}

const MobileNavLink = ({ to, active, children, highlight }: MobileNavLinkProps) => {
  return (
    <Link
      to={to}
      className={cn(
        'flex items-center px-4 py-2 text-sm font-medium rounded-md transition-all',
        active
          ? 'bg-accent text-white'
          : highlight
            ? 'bg-accent/10 text-accent hover:bg-accent/20'
            : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
      )}
    >
      {children}
    </Link>
  );
};

export default Navbar;
